import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DarkModeContext } from "../../context/DarkModeContext";
import { getToken } from "../../utils/auth";
import {
    defaultFilters,
    fetchCampaignOptions,
    postZocoMetaIaAnalisis,
    fetchZocoMetaAiKnowledge,
    postZocoMetaAiKnowledge,
    putZocoMetaAiKnowledge,
    deleteZocoMetaAiKnowledge,
    fetchZocoMetaAiHistorial,
    fetchZocoMetaAiFeedback,
    postZocoMetaAiFeedback,
    fetchZocoMetaAiDebugContext,
    fetchZocoMetaAiHistorialReporte,
} from "./dashboardApi";
import { useMetaAdAccounts } from "./metaAdAccounts";
import { Icono } from "./iconos";
import {
    ZMLayout,
    ZMCard,
    ZMFiltersBar,
    ZMEmptyState,
    ZMLoadingState,
    ZMErrorState,
    ZMMetricCard,
    ZMBadge,
    ZMTable,
} from "./components/ZMUI";

const feedbackOptions = [
    { value: "Buena", label: "Buena" },
    { value: "NoAplica", label: "No aplica" },
    { value: "YaSeHizo", label: "Ya se hizo" },
    { value: "Funciono", label: "Funciono" },
    { value: "NoFunciono", label: "No funciono" },
];

const tabs = [
    { key: "coordinador", label: "Coordinador" },
    { key: "paidMedia", label: "Paid Media" },
    { key: "marketing", label: "Marketing" },
    { key: "closers", label: "Closers" },
    { key: "historial", label: "Historial" },
    { key: "auditoria", label: "Auditoría" },
];

const fmtNum = (value, digits = 0) => {
    const n = Number(value || 0);
    return Number.isFinite(n)
        ? n.toLocaleString("es-AR", { minimumFractionDigits: digits, maximumFractionDigits: digits })
        : "0";
};
const fmtUsd = (value) => `US$ ${fmtNum(value, 2)}`;
const fmtPct = (value) => `${fmtNum(value, 2)}%`;

const normalizeDecision = (item, fallbackResponsable = "Paid Media") => {
    if (typeof item === "string") {
        return { accion: item, motivo: "Sugerido por IA.", impactoEsperado: "Mejorar resultados.", responsableSugerido: fallbackResponsable, prioridad: "media" };
    }
    return {
        accion: item?.accionSugerida || item?.accion || item?.titulo || item?.tarea || "Accion recomendada",
        motivo: item?.motivo || item?.detalle || "Sin motivo explicitado.",
        impactoEsperado: item?.impactoEsperado || item?.impacto || "Mejorar rendimiento.",
        responsableSugerido: item?.responsableSugerido || item?.responsable || item?.area || fallbackResponsable,
        prioridad: item?.prioridad || "media",
    };
};

const toDecisionList = (items, fallback = "Paid Media") =>
    Array.isArray(items) ? items.map((x) => normalizeDecision(x, fallback)) : [];

const scoreClass = (estado) => {
    const v = String(estado || "").toLowerCase();
    if (v.includes("crit")) return "is-critical";
    if (v.includes("escal")) return "is-scalable";
    if (v.includes("excel")) return "is-scalable";
    if (v.includes("buen")) return "is-good";
    return "is-improvable";
};

const alertTone = (tipo) => {
    const t = String(tipo || "").toLowerCase();
    if (t === "urgente") return "urgente";
    if (t === "oportunidad") return "oportunidad";
    return "revisar";
};

const prioridadColor = (p) => {
    const v = String(p || "").toLowerCase();
    if (v === "alta" || v === "urgente") return "#ef4444";
    if (v === "media") return "#f59e0b";
    return "#6b7280";
};

const Field = ({ label, value, onChange, type = "text", placeholder = "" }) => (
    <label className="d-flex flex-column" style={{ gap: 4, minWidth: 150 }}>
        <span style={{ fontSize: "0.75rem", color: "#6b7280", fontWeight: 600 }}>{label}</span>
        <input
            type={type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="form-control form-control-sm"
            placeholder={placeholder}
        />
    </label>
);

const CampaignSelect = ({ value, onChange, campaigns, loading }) => (
    <label className="d-flex flex-column" style={{ gap: 4, minWidth: 220 }}>
        <span style={{ fontSize: "0.75rem", color: "#6b7280", fontWeight: 600 }}>
            Campaña {loading && <span style={{ fontSize: "0.7rem" }}>(cargando...)</span>}
        </span>
        <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="form-control form-control-sm"
            disabled={loading}
        >
            <option value="">Todas las campañas</option>
            {campaigns.map((c) => (
                <option key={c.id} value={c.id}>
                    {c.label || `${c.nombre}${c.estado ? ` | ${c.estado}` : ""} | ${c.id}`}
                </option>
            ))}
        </select>
    </label>
);

export default function ZMIa() {
    const { darkMode } = useContext(DarkModeContext);
    const navigate = useNavigate();

    const [filters, setFilters] = useState(defaultFilters());
    const { accounts, selectedAccount, selectedKey, selectAccount, loading: loadingAccounts } = useMetaAdAccounts();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [data, setData] = useState(null);
    const [tab, setTab] = useState("coordinador");

    const [campaigns, setCampaigns] = useState([]);
    const [campaignsLoading, setCampaignsLoading] = useState(false);

    const [savingFeedbackKey, setSavingFeedbackKey] = useState("");
    const [feedbackMessage, setFeedbackMessage] = useState("");

    const [knowledge, setKnowledge] = useState([]);
    const [historial, setHistorial] = useState([]);
    const [feedbackRows, setFeedbackRows] = useState([]);
    const [learningLoading, setLearningLoading] = useState(false);
    const [learningError, setLearningError] = useState("");
    const [ruleForm, setRuleForm] = useState({ id: 0, categoria: "General", titulo: "", regla: "", prioridad: 1, activo: true });
    const [ruleSaving, setRuleSaving] = useState(false);

    const [debugContext, setDebugContext] = useState(null);
    const [debugLoading, setDebugLoading] = useState(false);
    const [debugError, setDebugError] = useState("");

    const [latestGeneratedReport, setLatestGeneratedReport] = useState(null);
    const [selectedReport, setSelectedReport] = useState(null);
    const [reportLoading, setReportLoading] = useState(false);

    const setFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

    const loadCampaigns = useCallback(async (currentFilters) => {
        const token = getToken();
        if (!token) return;
        setCampaignsLoading(true);
        try {
            const list = await fetchCampaignOptions(token, currentFilters);
            setCampaigns(Array.isArray(list) ? list : []);
        } catch {
            setCampaigns([]);
        } finally {
            setCampaignsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!selectedAccount) return;
        loadCampaigns(filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.fechaDesde, filters.fechaHasta, selectedKey]);

    const paidMedia = useMemo(() => {
        const agent = data?.agentes?.paidMedia || {};
        return {
            estado: agent.estado || "ok",
            error: agent.error || "",
            diagnostico: agent.diagnostico || data?.paidMedia?.diagnostico || "",
            campaniasAEscalar: toDecisionList(agent.campaniasAEscalar || data?.paidMedia?.campaniasAEscalar, "Paid Media"),
            campaniasAPausar: toDecisionList(agent.campaniasAPausar || data?.paidMedia?.campaniasAPausar || data?.paidMedia?.campaniasAPausarORevisar, "Paid Media"),
            campaniasAOptimizar: toDecisionList(agent.campaniasAOptimizar || data?.paidMedia?.campaniasAOptimizar, "Paid Media"),
            creatividadesGanadoras: toDecisionList(agent.creatividadesGanadoras || data?.paidMedia?.creatividadesGanadoras, "Marketing"),
            creatividadesConProblemas: toDecisionList(agent.creatividadesConProblemas || data?.paidMedia?.creatividadesConProblemas, "Marketing"),
            alertas: toDecisionList(agent.alertas || data?.paidMedia?.alertas, "Paid Media"),
            accionesPrioritarias: toDecisionList(agent.accionesPrioritarias || data?.paidMedia?.accionesPrioritarias, "Paid Media"),
            metricasClave: Array.isArray(agent.metricasClave) ? agent.metricasClave : [],
            hipotesis: Array.isArray(agent.hipotesis) && agent.hipotesis.length > 0 ? agent.hipotesis : (Array.isArray(data?.paidMedia?.hipotesis) ? data.paidMedia.hipotesis : []),
            experimentosRecomendados: toDecisionList(agent.experimentosRecomendados || data?.paidMedia?.experimentos, "Paid Media"),
        };
    }, [data]);

    const marketing = useMemo(() => {
        const agent = data?.agentes?.marketing || {};
        return {
            estado: agent.estado || "ok",
            error: agent.error || "",
            diagnostico: agent.diagnostico || data?.marketing?.diagnostico || "",
            insightsDeAudiencia: Array.isArray(agent.insightsDeAudiencia) ? agent.insightsDeAudiencia : [],
            ideasCreativas: toDecisionList(agent.ideasCreativas || data?.marketing?.ideasCreativas, "Marketing"),
            mensajesSugeridos: toDecisionList(agent.mensajesSugeridos || data?.marketing?.mensajesSugeridos, "Marketing"),
            angulosDeVenta: toDecisionList(agent.angulosDeVenta || data?.marketing?.angulosDeVenta, "Marketing"),
            ofertasAProbar: toDecisionList(agent.ofertasAProbar || data?.marketing?.ofertasAProbar, "Marketing"),
            alertas: toDecisionList(agent.alertas || data?.marketing?.alertas, "Marketing"),
            accionesPrioritarias: toDecisionList(agent.accionesPrioritarias || data?.marketing?.accionesPrioritarias, "Marketing"),
            experimentosRecomendados: toDecisionList(agent.experimentosRecomendados || data?.marketing?.experimentos, "Marketing"),
        };
    }, [data]);

    const closers = useMemo(() => {
        const agent = data?.agentes?.closers || {};
        return {
            estado: agent.estado || "ok",
            error: agent.error || "",
            diagnostico: agent.diagnostico || data?.closers?.diagnostico || "",
            leadsPrioritarios: toDecisionList(agent.leadsPrioritarios || data?.closers?.leadsPrioritarios, "Closers"),
            leadsCriticos: toDecisionList(agent.leadsCriticos || data?.closers?.leadsCriticos, "Closers"),
            alertasSeguimiento: toDecisionList(agent.alertasSeguimiento || data?.closers?.alertasSeguimiento, "Closers"),
            accionesPrioritarias: toDecisionList(agent.accionesPrioritarias || data?.closers?.accionesPrioritarias, "Closers"),
            scriptsDeContacto: Array.isArray(agent.scriptsDeContacto) && agent.scriptsDeContacto.length > 0 ? agent.scriptsDeContacto : (Array.isArray(data?.closers?.scriptsSugeridos) ? data.closers.scriptsSugeridos : []),
            objecionesProbables: Array.isArray(agent.objecionesProbables) && agent.objecionesProbables.length > 0 ? agent.objecionesProbables : (Array.isArray(data?.closers?.objecionesYRespuestas) ? data.closers.objecionesYRespuestas : []),
            mejorasProcesoComercial: Array.isArray(agent.mejorasProcesoComercial) ? agent.mejorasProcesoComercial : [],
            slaRecomendado: agent.slaRecomendado || "",
            checklistCalidad: Array.isArray(agent.checklistCalidad) && agent.checklistCalidad.length > 0 ? agent.checklistCalidad : (Array.isArray(data?.closers?.checklistCalidad) ? data.closers.checklistCalidad : []),
            sla: Array.isArray(agent.sla) && agent.sla.length > 0 ? agent.sla : (Array.isArray(data?.closers?.sla) ? data.closers.sla : []),
        };
    }, [data]);

    const decisionFinal = useMemo(() => {
        const d = data?.decisionFinal || {};
        const coordData = data?.coordinador || {};
        return {
            queHacerHoy: toDecisionList(d.queHacerHoy || coordData.queHacerHoy, "Paid Media"),
            quePausar: toDecisionList(d.quePausar || coordData.quePausar, "Paid Media"),
            queEscalar: toDecisionList(d.queEscalar || coordData.queEscalar, "Paid Media"),
            queRevisar: toDecisionList(d.queRevisar || coordData.queRevisar, "Marketing"),
            responsables: Array.isArray(d.responsables) ? d.responsables : [],
            impactoEsperado: Array.isArray(d.impactoEsperado) ? d.impactoEsperado : [],
        };
    }, [data]);

    const auditoriaDatos = useMemo(() => ({
        resumen: data?.auditoriaDatos?.resumen || "",
        checksConsistencia: Array.isArray(data?.auditoriaDatos?.checksConsistencia) ? data.auditoriaDatos.checksConsistencia : [],
        crmVsMeta: Array.isArray(data?.auditoriaDatos?.crmVsMeta) ? data.auditoriaDatos.crmVsMeta : [],
        camposFaltantes: Array.isArray(data?.auditoriaDatos?.camposFaltantes) ? data.auditoriaDatos.camposFaltantes : [],
    }), [data]);

    const loadLearning = async (token, analisisId = null) => {
        setLearningLoading(true);
        setLearningError("");
        try {
            const [knowledgeRows, historialRows, feedback] = await Promise.all([
                fetchZocoMetaAiKnowledge(token),
                fetchZocoMetaAiHistorial(token, 100),
                fetchZocoMetaAiFeedback(token, analisisId || undefined),
            ]);
            setKnowledge(Array.isArray(knowledgeRows) ? knowledgeRows : []);
            setHistorial(Array.isArray(historialRows) ? historialRows : []);
            setFeedbackRows(Array.isArray(feedback) ? feedback : []);
        } catch {
            setLearningError("No se pudo cargar aprendizaje IA.");
        } finally {
            setLearningLoading(false);
        }
    };

    const generar = async () => {
        const token = getToken();
        if (!token) return navigate("/");

        setLoading(true);
        setError("");
        setFeedbackMessage("");

        try {
            const payload = await postZocoMetaIaAnalisis(token, filters);
            setData(payload);
            setLatestGeneratedReport(payload);
            setSelectedReport(null);
            setTab("coordinador");
            await loadLearning(token, payload?.analisisId || null);
        } catch (e) {
            if (e?.code === 401 || e?.code === 403 || e?.message === "unauthorized") return navigate("/");
            setError(e?.message || "No se pudo generar el analisis IA.");
        } finally {
            setLoading(false);
        }
    };

    const loadDebugContext = async () => {
        const token = getToken();
        if (!token) return navigate("/");
        setDebugLoading(true);
        setDebugError("");
        try {
            const ctx = await fetchZocoMetaAiDebugContext(token, filters);
            setDebugContext(ctx);
        } catch (e) {
            if (e?.code === 401 || e?.code === 403 || e?.message === "unauthorized") return navigate("/");
            setDebugError("No se pudo cargar la auditoría de datos.");
        } finally {
            setDebugLoading(false);
        }
    };

    const loadHistorialReporte = async (id) => {
        const token = getToken();
        if (!token) return;
        setReportLoading(true);
        try {
            const r = await fetchZocoMetaAiHistorialReporte(token, id);
            setData(r);
            setSelectedReport(null);
            setTab("coordinador");
            await loadLearning(token, r?.analisisId || null);
        } catch {
            setFeedbackMessage("No se pudo cargar el reporte historico.");
        } finally {
            setReportLoading(false);
        }
    };

    const volverAnalisisActual = () => {
        if (!latestGeneratedReport) return;
        setData(latestGeneratedReport);
        setTab("coordinador");
    };

    const enviarFeedback = async (agente, recomendacion, feedback) => {
        const token = getToken();
        if (!token || !data) return;

        const rec = normalizeDecision(recomendacion, agente);
        const key = `${agente}:${rec.accion}:${feedback}`;
        setSavingFeedbackKey(key);
        setFeedbackMessage("");
        try {
            await postZocoMetaAiFeedback({
                token,
                analisisId: data.analisisId || 0,
                agente,
                recomendacion: `${rec.accion} | Motivo: ${rec.motivo}`,
                feedback,
                comentario: "",
            });
            setFeedbackMessage("Feedback guardado.");
            await loadLearning(token, data.analisisId || null);
        } catch {
            setFeedbackMessage("No se pudo guardar feedback.");
        } finally {
            setSavingFeedbackKey("");
        }
    };

    const onSaveRule = async () => {
        const token = getToken();
        if (!token) return navigate("/");
        if (!ruleForm.titulo.trim() || !ruleForm.regla.trim()) {
            setLearningError("Titulo y regla son obligatorios.");
            return;
        }
        setRuleSaving(true);
        setLearningError("");
        try {
            const payload = {
                token,
                categoria: ruleForm.categoria,
                titulo: ruleForm.titulo.trim(),
                regla: ruleForm.regla.trim(),
                prioridad: Number(ruleForm.prioridad || 1),
                activo: !!ruleForm.activo,
            };
            if (ruleForm.id > 0) await putZocoMetaAiKnowledge(ruleForm.id, payload);
            else await postZocoMetaAiKnowledge(payload);
            setRuleForm({ id: 0, categoria: "General", titulo: "", regla: "", prioridad: 1, activo: true });
            await loadLearning(token, data?.analisisId || null);
        } catch {
            setLearningError("No se pudo guardar la regla.");
        } finally {
            setRuleSaving(false);
        }
    };

    const onDeleteRule = async (id) => {
        const token = getToken();
        if (!token) return navigate("/");
        try {
            await deleteZocoMetaAiKnowledge(token, id);
            await loadLearning(token, data?.analisisId || null);
        } catch {
            setLearningError("No se pudo eliminar la regla.");
        }
    };

    const exportarReporteHtml = (reportData, titulo) => {
        const d = reportData || data;
        if (!d) return;
        const coord = d.coordinador || {};
        const scoreValue = coord.scoreGeneral > 0 ? coord.scoreGeneral : d.score?.valor || 0;
        const scoreLabel = coord.estadoGeneral || d.score?.estado || "Mejorable";
        const fecha = new Date().toLocaleString("es-AR");

        const renderItems = (items) => {
            if (!Array.isArray(items) || items.length === 0) return "<p style='color:#6b7280;font-size:0.85rem'>Sin recomendaciones registradas.</p>";
            return items.map((item) => {
                const rec = normalizeDecision(item);
                return `
                <div style="background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;padding:12px;margin-bottom:10px;">
                    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px;">
                        <strong style="font-size:0.95rem">${rec.accion}</strong>
                        <span style="background:${prioridadColor(rec.prioridad)};color:#fff;border-radius:4px;padding:2px 8px;font-size:0.75rem;font-weight:600;">${String(rec.prioridad).toUpperCase()}</span>
                    </div>
                    <p style="color:#6b7280;font-size:0.85rem;margin:4px 0"><strong>Motivo:</strong> ${rec.motivo}</p>
                    <p style="font-size:0.85rem;margin:4px 0"><strong>Impacto esperado:</strong> ${rec.impactoEsperado}</p>
                    <p style="font-size:0.85rem;margin:0"><strong>Responsable:</strong> ${rec.responsableSugerido}</p>
                </div>`;
            }).join("");
        };

        const sectionTitle = (t) => `<h3 style="font-size:1rem;font-weight:700;margin:20px 0 10px;padding-bottom:6px;border-bottom:2px solid #10b981;color:#065f46">${t}</h3>`;

        const html = `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="UTF-8"/>
<title>Centro de Decisiones IA – Zoco Meta</title>
<style>
  body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;margin:0;padding:32px;background:#f3f4f6;color:#111827;}
  .header{background:linear-gradient(135deg,#065f46,#10b981);color:#fff;border-radius:12px;padding:28px 32px;margin-bottom:28px;}
  .header h1{font-size:1.6rem;font-weight:800;margin:0 0 4px}
  .header p{font-size:0.9rem;opacity:.85;margin:0}
  .score-box{display:inline-block;background:rgba(255,255,255,.15);border-radius:8px;padding:12px 20px;margin-top:14px;}
  .score-box .val{font-size:2.5rem;font-weight:900;line-height:1}
  .score-box .lbl{font-size:0.85rem;font-weight:600;margin-top:4px}
  .kpis{display:grid;grid-template-columns:repeat(auto-fill,minmax(160px,1fr));gap:14px;margin-bottom:24px;}
  .kpi{background:#fff;border-radius:10px;padding:16px;box-shadow:0 1px 4px rgba(0,0,0,.08);}
  .kpi .lbl{font-size:0.75rem;color:#6b7280;font-weight:600;margin-bottom:4px}
  .kpi .val{font-size:1.4rem;font-weight:800;color:#065f46}
  .card{background:#fff;border-radius:10px;padding:20px;box-shadow:0 1px 4px rgba(0,0,0,.08);margin-bottom:18px;}
  .alerta{display:flex;gap:12px;padding:10px 14px;background:#fef3c7;border-radius:8px;margin-bottom:8px;border-left:4px solid #f59e0b;}
  .alerta.urgente{background:#fee2e2;border-color:#ef4444;}
  .alerta.oportunidad{background:#d1fae5;border-color:#10b981;}
  table{width:100%;border-collapse:collapse;font-size:0.85rem;}
  th{background:#f9fafb;text-align:left;padding:8px 12px;font-weight:700;border-bottom:2px solid #e5e7eb;}
  td{padding:8px 12px;border-bottom:1px solid #f3f4f6;}
  .footer{text-align:center;color:#9ca3af;font-size:0.8rem;margin-top:40px;padding-top:20px;border-top:1px solid #e5e7eb;}
</style>
</head>
<body>
<div class="header">
  <h1>${titulo || "Centro de Decisiones IA – Zoco Meta"}</h1>
  <p>Generado el ${fecha}</p>
  <div class="score-box">
    <div class="val">${scoreValue}</div>
    <div class="lbl">${scoreLabel}</div>
  </div>
</div>

<div class="kpis">
  <div class="kpi"><div class="lbl">Leads CRM</div><div class="val">${fmtNum(d.kpis?.leadsTotales)}</div></div>
  <div class="kpi"><div class="lbl">Inversión</div><div class="val">${fmtUsd(d.kpis?.inversionTotal)}</div></div>
  <div class="kpi"><div class="lbl">CPL promedio</div><div class="val">${fmtUsd(d.kpis?.cplPromedio)}</div></div>
  <div class="kpi"><div class="lbl">CTR promedio</div><div class="val">${fmtPct(d.kpis?.ctrPromedio)}</div></div>
  <div class="kpi"><div class="lbl">Contactados</div><div class="val">${fmtNum(d.kpis?.contactados)}</div></div>
  <div class="kpi"><div class="lbl">Cerrados</div><div class="val">${fmtNum(d.kpis?.cerrados)}</div></div>
  <div class="kpi"><div class="lbl">Tasa contacto</div><div class="val">${fmtPct(d.kpis?.tasaContacto)}</div></div>
  <div class="kpi"><div class="lbl">Tasa cierre</div><div class="val">${fmtPct(d.kpis?.tasaCierre)}</div></div>
</div>

<div class="card">
  <h2 style="font-size:1.1rem;font-weight:700;margin:0 0 10px">Resumen ejecutivo del Coordinador</h2>
  <p style="font-size:0.9rem;margin:0 0 10px">${coord.resumenEjecutivo || d.resumenGeneral || "-"}</p>
  <p style="font-size:0.85rem;margin:4px 0"><strong>Prioridad máxima:</strong> ${coord.prioridadMaxima || "-"}</p>
  <p style="font-size:0.85rem;margin:4px 0"><strong>Decisión hoy:</strong> ${coord.decisionRecomendadaHoy || "-"}</p>
  <p style="font-size:0.85rem;margin:4px 0"><strong>Riesgo principal:</strong> ${coord.riesgoPrincipal || "-"}</p>
  <p style="font-size:0.85rem;margin:0"><strong>Oportunidad principal:</strong> ${coord.oportunidadPrincipal || "-"}</p>
</div>

<div class="card">
  <h2 style="font-size:1.1rem;font-weight:700;margin:0 0 10px">Alertas inteligentes</h2>
  ${(d.alertas || []).map((a) => `
  <div class="alerta ${alertTone(a.tipo)}">
    <strong>${a.tipo || "revisar"}</strong>
    <div>
      <strong>${a.titulo}</strong>
      <p style="margin:2px 0 0;font-size:0.85rem">${a.descripcion}</p>
      <small style="color:#6b7280">${a.area || ""}</small>
    </div>
  </div>`).join("") || "<p style='color:#6b7280;font-size:0.85rem'>Sin alertas.</p>"}
</div>

${sectionTitle("Que hacer hoy (Coordinador)")}
${renderItems((d.decisionFinal || {}).queHacerHoy)}

${sectionTitle("Agente Paid Media – Diagnóstico y acciones")}
<div class="card"><p style="font-size:0.9rem;margin:0">${d.agentes?.paidMedia?.diagnostico || d.paidMedia?.diagnostico || "-"}</p></div>
${sectionTitle("Campañas a escalar")}
${renderItems(d.agentes?.paidMedia?.campaniasAEscalar)}
${sectionTitle("Campañas a pausar")}
${renderItems(d.agentes?.paidMedia?.campaniasAPausar)}
${sectionTitle("Campañas a optimizar")}
${renderItems(d.agentes?.paidMedia?.campaniasAOptimizar || d.paidMedia?.campaniasAOptimizar)}
${sectionTitle("Acciones prioritarias Paid Media")}
${renderItems(d.agentes?.paidMedia?.accionesPrioritarias)}

${sectionTitle("Agente Marketing – Diagnóstico y acciones")}
<div class="card"><p style="font-size:0.9rem;margin:0">${d.agentes?.marketing?.diagnostico || d.marketing?.diagnostico || "-"}</p></div>
${sectionTitle("Ideas creativas")}
${renderItems(d.agentes?.marketing?.ideasCreativas)}
${sectionTitle("Mensajes sugeridos")}
${renderItems(d.agentes?.marketing?.mensajesSugeridos)}
${sectionTitle("Ángulos de venta")}
${renderItems(d.agentes?.marketing?.angulosDeVenta || d.marketing?.angulosDeVenta)}
${sectionTitle("Acciones prioritarias Marketing")}
${renderItems(d.agentes?.marketing?.accionesPrioritarias)}

${sectionTitle("Agente Closers / Ventas – Diagnóstico y acciones")}
<div class="card"><p style="font-size:0.9rem;margin:0">${d.agentes?.closers?.diagnostico || d.closers?.diagnostico || "-"}</p></div>
${sectionTitle("Leads críticos")}
${renderItems(d.agentes?.closers?.leadsCriticos)}
${sectionTitle("Leads prioritarios")}
${renderItems(d.agentes?.closers?.leadsPrioritarios)}
${sectionTitle("Acciones prioritarias Closers")}
${renderItems(d.agentes?.closers?.accionesPrioritarias)}

${sectionTitle("Checklist comercial")}
<div class="card">
  <ul style="padding-left:20px;margin:0">
    ${((d.agentes?.closers?.checklistCalidad || d.closers?.checklistCalidad || []).map((p) => `<li style="font-size:0.9rem;margin-bottom:6px">${p}</li>`).join("")) || "<li style='color:#6b7280'>Sin checklist informado.</li>"}
  </ul>
</div>

${sectionTitle("Auditoría de datos")}
<div class="card">
  <p style="font-size:0.9rem;margin:0 0 10px">${d.auditoriaDatos?.resumen || "Sin observaciones adicionales."}</p>
  <ul style="padding-left:20px;margin:0">
    ${((d.auditoriaDatos?.camposFaltantes || d.datosFaltantes || []).map((p) => `<li style="font-size:0.85rem;margin-bottom:4px">${p}</li>`).join("")) || "<li style='color:#6b7280'>Sin faltantes reportados.</li>"}
  </ul>
</div>

${sectionTitle("Próximos pasos")}
<div class="card">
  <ol style="padding-left:20px;margin:0">
    ${(d.proximosPasos || []).map((p) => `<li style="font-size:0.9rem;margin-bottom:6px">${p}</li>`).join("") || "<li style='color:#6b7280'>Sin próximos pasos definidos.</li>"}
  </ol>
</div>

${sectionTitle("Datos faltantes / Limitaciones")}
<div class="card">
  <ul style="padding-left:20px;margin:0">
    ${(d.datosFaltantes || d.aprendizaje?.datosFaltantes || []).map((p) => `<li style="font-size:0.85rem;color:#ef4444;margin-bottom:4px">${p}</li>`).join("") || "<li style='color:#6b7280'>Sin datos faltantes reportados.</li>"}
  </ul>
</div>

<div class="footer">
  Generado por Centro de Decisiones IA – Zoco Meta &nbsp;|&nbsp; ${fecha}
</div>
</body>
</html>`;

        const blob = new Blob([html], { type: "text/html;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `zocometa-ia-${new Date().toISOString().slice(0, 10)}.html`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const motivosDatosFaltantes = (items = []) =>
        (Array.isArray(items) ? items : [])
            .filter((item) => item && !String(item).startsWith("RESPUESTA_IA_INCOMPLETA"))
            .slice(0, 2);

    const renderEmptySection = () => (
        <div className="text-muted" style={{ fontSize: "0.85rem" }}>
            <div>No hay datos suficientes para esta sección.</div>
            {motivosDatosFaltantes(data?.datosFaltantes).map((item, idx) => (
                <div key={`empty-reason-${idx}`} style={{ marginTop: 4 }}>
                    {item}
                </div>
            ))}
        </div>
    );

    const renderDecisionList = (title, rows, agente) => (
        <ZMCard title={title} className="mb-3">
            {!rows?.length && renderEmptySection()}
            {!!rows?.length && (
                <div className="zm-ia-decision-list">
                    {rows.map((row, idx) => {
                        const rec = normalizeDecision(row, agente);
                        return (
                            <div className="zm-ia-decision-item" key={`${title}-${idx}-${rec.accion}`}>
                                <div className="d-flex align-items-start justify-content-between gap-2">
                                    <strong style={{ fontSize: "0.9rem", lineHeight: 1.4 }}>{rec.accion}</strong>
                                    <ZMBadge status={rec.prioridad} label={String(rec.prioridad).toUpperCase()} />
                                </div>
                                <div className="small text-muted mt-1">{rec.motivo}</div>
                                <div style={{ fontSize: "0.82rem", marginTop: 4 }}>
                                    <span style={{ color: "#059669" }}>Impacto:</span> {rec.impactoEsperado}
                                    <br />
                                    <span style={{ color: "#6366f1" }}>Responsable:</span> {rec.responsableSugerido}
                                </div>
                                <div className="zm-ia-feedback-btns mt-2">
                                    {feedbackOptions.map((opt) => {
                                        const key = `${agente}:${rec.accion}:${opt.value}`;
                                        return (
                                            <button
                                                key={key}
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary"
                                                style={{ fontSize: "0.75rem", padding: "2px 8px" }}
                                                disabled={savingFeedbackKey === key}
                                                onClick={() => enviarFeedback(agente, rec, opt.value)}
                                            >
                                                {opt.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </ZMCard>
    );

    const renderAgentFailure = (estado, error, label) => (
        estado === "fallido" ? (
            <div className="alert alert-warning py-2 px-3 mb-3 small">
                <strong>{label} con error.</strong> {error || "La IA no devolvio un JSON valido para este agente."}
            </div>
        ) : null
    );

    const coord = data?.coordinador || {};
    const scoreValue = coord.scoreGeneral > 0 ? coord.scoreGeneral : data?.score?.valor || 0;
    const scoreLabel = coord.estadoGeneral || data?.score?.estado || "Mejorable";
    const isViewingHistorical = data?.source === "historial";
    const canRestoreCurrent = !!latestGeneratedReport && latestGeneratedReport?.analisisId !== data?.analisisId;

    const renderChecksBadge = (tipo) => {
        if (tipo === "urgente" || tipo === "ineficiencia") return "danger";
        if (tipo === "warning" || tipo === "datos_faltantes") return "warning";
        return "secondary";
    };

    return (
        <ZMLayout
            title="Centro de decisiones IA"
            subtitle="Comité IA de Paid Media, Marketing, Closers y Coordinador general."
            right={selectedAccount ? `${selectedAccount.name} (${selectedAccount.adAccountId})` : (loading ? "Generando..." : "Actualizado")}
            className={darkMode ? "container-dark" : ""}
        >
            {/* Filtros */}
            <ZMCard className="mb-3" bodyClassName="p-3">
                <ZMFiltersBar>
                    <label className="d-flex flex-column" style={{ gap: 4, minWidth: 260 }}>
                        <span style={{ fontSize: "0.75rem", color: "#6b7280", fontWeight: 600 }}>
                            Cuenta publicitaria {loadingAccounts && <span style={{ fontSize: "0.7rem" }}>(cargando...)</span>}
                        </span>
                        <select value={selectedKey} onChange={(e) => selectAccount(e.target.value)} className="form-control form-control-sm" disabled={loadingAccounts || accounts.length === 0}>
                            {accounts.map((item) => (
                                <option key={item.key} value={item.key}>
                                    {item.name} ({item.adAccountId})
                                </option>
                            ))}
                        </select>
                    </label>
                    <Field label="Desde" type="date" value={filters.fechaDesde} onChange={(v) => setFilter("fechaDesde", v)} />
                    <Field label="Hasta" type="date" value={filters.fechaHasta} onChange={(v) => setFilter("fechaHasta", v)} />
                    <CampaignSelect
                        value={filters.campania}
                        onChange={(v) => setFilter("campania", v)}
                        campaigns={campaigns}
                        loading={campaignsLoading}
                    />
                    <Field label="Estado" value={filters.estado} onChange={(v) => setFilter("estado", v)} placeholder="nuevo/contactado..." />
                    <Field label="Buscar" value={filters.buscar} onChange={(v) => setFilter("buscar", v)} placeholder="Nombre/email..." />
                    <div className="d-flex align-items-end gap-2 flex-wrap">
                        <button type="button" className="btn btn-sm zm-btn-primary" onClick={generar} disabled={loading}>
                            {loading ? "Generando..." : "Generar análisis"}
                        </button>
                        <button type="button" className="btn btn-sm btn-outline-success" onClick={() => exportarReporteHtml()} disabled={!data || loading}>
                            Exportar HTML
                        </button>
                    </div>
                </ZMFiltersBar>
            </ZMCard>

            {loading && <ZMLoadingState label="Procesando datos reales Meta + CRM con IA..." fullScreen />}
            {!loading && error && <ZMErrorState message={error} onRetry={generar} />}
            {!loading && !error && !data && (
                <ZMEmptyState
                    title="Sin análisis generado"
                    hint="Seleccioná el rango de fechas y la campaña, luego presioná 'Generar análisis' para obtener decisiones accionables por agente."
                />
            )}

            {!loading && !error && data && (
                <>
                    {isViewingHistorical && (
                        <div className="alert alert-secondary d-flex flex-wrap align-items-center gap-2 py-2 px-3 mb-3">
                            <strong>
                                Viendo reporte histórico #{data.analisisId || data.id}
                            </strong>
                            <span className="small text-muted">
                                generado el {data.fechaCreacion ? new Date(data.fechaCreacion).toLocaleString("es-AR") : "-"}
                            </span>
                            {canRestoreCurrent && (
                                <button type="button" className="btn btn-sm btn-outline-primary ms-auto" onClick={volverAnalisisActual}>
                                    Volver al análisis actual
                                </button>
                            )}
                        </div>
                    )}

                    {/* KPIs */}
                    <div className="zm-grid-metrics mb-3">
                        <ZMMetricCard icon={<Icono.Users />} label="Leads CRM" value={fmtNum(data.kpis?.leadsTotales)} />
                        <ZMMetricCard icon={<Icono.Wallet />} label="Inversión" value={fmtUsd(data.kpis?.inversionTotal)} />
                        <ZMMetricCard icon={<Icono.Dollar />} label="CPL promedio" value={fmtUsd(data.kpis?.cplPromedio)} />
                        <ZMMetricCard icon={<Icono.MousePointer />} label="CTR promedio" value={fmtPct(data.kpis?.ctrPromedio)} />
                        <ZMMetricCard icon={<Icono.Phone />} label="Contactados" value={fmtNum(data.kpis?.contactados)} />
                        <ZMMetricCard icon={<Icono.CheckCircle />} label="Cerrados" value={fmtNum(data.kpis?.cerrados)} />
                        <ZMMetricCard icon={<Icono.Target />} label="Tasa contacto" value={fmtPct(data.kpis?.tasaContacto)} />
                        <ZMMetricCard icon={<Icono.TrendingUp />} label="Tasa cierre" value={fmtPct(data.kpis?.tasaCierre)} />
                    </div>

                    {/* Score + Alertas */}
                    <div className="row g-3 mb-3">
                        <div className="col-12 col-lg-4">
                            <ZMCard title="Score general" subtitle={coord.diagnosticoGeneral || data.score?.motivo || "-"} className="h-100">
                                <div className="zm-ia-score">
                                    <div className="zm-ia-score-value">{fmtNum(scoreValue)}</div>
                                    <div className={`zm-ia-score-chip ${scoreClass(scoreLabel)}`}>{scoreLabel}</div>
                                </div>
                                <hr />
                                <div className="small">
                                    <div><strong>Prioridad máxima:</strong> {coord.prioridadMaxima || "-"}</div>
                                    <div className="mt-1"><strong>Decisión hoy:</strong> {coord.decisionRecomendadaHoy || "-"}</div>
                                    {coord.riesgoPrincipal && <div className="mt-1" style={{ color: "#ef4444" }}><strong>Riesgo:</strong> {coord.riesgoPrincipal}</div>}
                                    {coord.oportunidadPrincipal && <div className="mt-1" style={{ color: "#059669" }}><strong>Oportunidad:</strong> {coord.oportunidadPrincipal}</div>}
                                </div>
                            </ZMCard>
                        </div>
                        <div className="col-12 col-lg-8">
                            <ZMCard title="Alertas inteligentes" className="h-100">
                                <div className="zm-ia-alert-list">
                                    {(data.alertas || []).map((a, idx) => (
                                        <div key={`alert-${idx}`} className="zm-ia-alert-item">
                                            <span className={`zm-ia-alert-badge ${alertTone(a.tipo)}`}>{a.tipo || "revisar"}</span>
                                            <div>
                                                <strong>{a.titulo}</strong>
                                                <p className="mb-1" style={{ fontSize: "0.85rem" }}>{a.descripcion}</p>
                                                <small className="text-muted">{a.area}</small>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </ZMCard>
                        </div>
                    </div>

                    {feedbackMessage && (
                        <div className="alert alert-info py-2 px-3 mb-3 small">{feedbackMessage}</div>
                    )}

                    {/* Banner respuesta IA incompleta */}
                    {(data.datosFaltantes || []).some(d => d.startsWith("RESPUESTA_IA_INCOMPLETA")) && (
                        <div className="alert alert-warning d-flex align-items-start gap-2 py-2 px-3 mb-3" style={{ fontSize: "0.85rem" }}>
                            <span style={{ fontSize: "1.1rem" }}>⚠️</span>
                            <div>
                                <strong>Respuesta IA incompleta.</strong>{" "}
                                Algunos tabs (Paid Media, Marketing, Closers) pueden estar vacíos.{" "}
                                Intentá <button type="button" className="btn btn-sm btn-warning py-0 px-2 ms-1" onClick={generar} disabled={loading}>Regenerar</button>{" "}
                                o aumentá <code>MaxOutputTokens</code> en la configuración.
                            </div>
                        </div>
                    )}

                    {/* Acciones rápidas */}
                    <ZMCard className="mb-3" bodyClassName="p-3">
                        <div className="d-flex flex-wrap gap-2">
                            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => navigate("/zocometa/leads")}>Ver leads críticos</button>
                            <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => navigate("/zocometa/campanas")}>Ver campañas</button>
                            <button type="button" className="btn btn-sm btn-outline-success" onClick={() => exportarReporteHtml()}>Exportar reporte HTML</button>
                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => { setTab("historial"); loadLearning(getToken(), data?.analisisId || null); }}>Ver historial</button>
                            <button type="button" className="btn btn-sm zm-btn-primary" onClick={generar} disabled={loading}>Regenerar análisis</button>
                        </div>
                    </ZMCard>

                    {/* Tabs */}
                    <div className="zm-ia-tabs mb-3">
                        {tabs.map((item) => (
                            <button
                                key={item.key}
                                type="button"
                                className={`btn btn-sm ${tab === item.key ? "zm-btn-primary" : "btn-outline-secondary"}`}
                                onClick={() => setTab(item.key)}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>

                    {/* ─── TAB COORDINADOR ─── */}
                    {tab === "coordinador" && (
                        <>
                            <ZMCard title="Resumen ejecutivo" subtitle={coord.diagnosticoGeneral || "-"} className="mb-3">
                                {renderAgentFailure(coord.estado, coord.error, "Coordinador")}
                                <p style={{ fontSize: "0.9rem", margin: 0 }}>{coord.resumenEjecutivo || data.resumenGeneral || "-"}</p>
                            </ZMCard>
                            <div className="row g-3 mt-1">
                                <div className="col-12 col-lg-6">{renderDecisionList("Que hacer hoy", decisionFinal.queHacerHoy, "Coordinador")}</div>
                                <div className="col-12 col-lg-6">{renderDecisionList("Que pausar", decisionFinal.quePausar, "Coordinador")}</div>
                                <div className="col-12 col-lg-6">{renderDecisionList("Que escalar", decisionFinal.queEscalar, "Coordinador")}</div>
                                <div className="col-12 col-lg-6">{renderDecisionList("Que revisar", decisionFinal.queRevisar, "Coordinador")}</div>
                            </div>
                            {(data.proximosPasos || []).length > 0 && (
                                <ZMCard title="Próximos pasos" className="mt-3">
                                    <ol style={{ paddingLeft: 20, margin: 0 }}>
                                        {(data.proximosPasos || []).map((p, i) => (
                                            <li key={i} style={{ fontSize: "0.9rem", marginBottom: 6 }}>{p}</li>
                                        ))}
                                    </ol>
                                </ZMCard>
                            )}
                            {(data.datosFaltantes || []).length > 0 && (
                                <ZMCard title="Datos faltantes detectados" className="mt-3">
                                    <ul style={{ paddingLeft: 20, margin: 0 }}>
                                        {(data.datosFaltantes || []).map((d, i) => (
                                            <li key={i} style={{ fontSize: "0.85rem", color: "#ef4444", marginBottom: 4 }}>{d}</li>
                                        ))}
                                    </ul>
                                </ZMCard>
                            )}
                            <ZMCard title="Responsables sugeridos" className="mt-3">
                                <div className="d-flex flex-wrap gap-2">
                                    {(decisionFinal.responsables || []).map((r, idx) => <ZMBadge key={`${r}-${idx}`} status="active" label={r} />)}
                                </div>
                            </ZMCard>
                        </>
                    )}

                    {/* ─── TAB PAID MEDIA ─── */}
                    {tab === "paidMedia" && (
                        <>
                            {renderAgentFailure(paidMedia.estado, paidMedia.error, "Paid Media")}
                            {paidMedia.accionesPrioritarias.length === 0 && paidMedia.campaniasAEscalar.length === 0 && (
                                <div className="alert alert-warning py-2 px-3 mb-3 small">
                                    ⚠️ <strong>Tab sin datos.</strong> La IA no generó recomendaciones para Paid Media en este análisis. Presioná <button type="button" className="btn btn-sm btn-warning py-0 px-2 ms-1" onClick={generar} disabled={loading}>Regenerar</button>
                                </div>
                            )}
                            <ZMCard title="Diagnóstico Paid Media" className="mb-3">
                                <p style={{ fontSize: "0.9rem", margin: "0 0 8px" }}>{paidMedia.diagnostico || "-"}</p>
                                {paidMedia.metricasClave.length > 0 && (
                                    <div className="d-flex flex-wrap gap-2 mt-2">
                                        {paidMedia.metricasClave.map((m, i) => <ZMBadge key={i} status="active" label={m} />)}
                                    </div>
                                )}
                            </ZMCard>
                            {renderDecisionList("Campañas a escalar", paidMedia.campaniasAEscalar, "PaidMedia")}
                            {renderDecisionList("Campañas a pausar", paidMedia.campaniasAPausar, "PaidMedia")}
                            {renderDecisionList("Campañas a optimizar", paidMedia.campaniasAOptimizar, "PaidMedia")}
                            {renderDecisionList("Creatividades ganadoras", paidMedia.creatividadesGanadoras, "PaidMedia")}
                            {renderDecisionList("Creatividades con problemas", paidMedia.creatividadesConProblemas, "PaidMedia")}
                            {renderDecisionList("Acciones prioritarias", paidMedia.accionesPrioritarias, "PaidMedia")}
                            {renderDecisionList("Alertas Paid Media", paidMedia.alertas, "PaidMedia")}
                            {paidMedia.hipotesis.length > 0 && (
                                <ZMCard title="Hipótesis a testear" className="mb-3">
                                    <ul style={{ paddingLeft: 20, margin: 0 }}>
                                        {paidMedia.hipotesis.map((h, i) => <li key={i} style={{ fontSize: "0.9rem", marginBottom: 6 }}>{h}</li>)}
                                    </ul>
                                </ZMCard>
                            )}
                            {renderDecisionList("Experimentos recomendados", paidMedia.experimentosRecomendados, "PaidMedia")}
                        </>
                    )}

                    {/* ─── TAB MARKETING ─── */}
                    {tab === "marketing" && (
                        <>
                            {renderAgentFailure(marketing.estado, marketing.error, "Marketing")}
                            {marketing.accionesPrioritarias.length === 0 && marketing.ideasCreativas.length === 0 && (
                                <div className="alert alert-warning py-2 px-3 mb-3 small">
                                    ⚠️ <strong>Tab sin datos.</strong> La IA no generó recomendaciones para Marketing en este análisis. Presioná <button type="button" className="btn btn-sm btn-warning py-0 px-2 ms-1" onClick={generar} disabled={loading}>Regenerar</button>
                                </div>
                            )}
                            <ZMCard title="Diagnóstico Marketing" className="mb-3">
                                <p style={{ fontSize: "0.9rem", margin: "0 0 8px" }}>{marketing.diagnostico || "-"}</p>
                                {marketing.insightsDeAudiencia.length > 0 && (
                                    <div className="mt-2">
                                        <strong className="small">Insights de audiencia:</strong>
                                        <ul style={{ paddingLeft: 18, margin: "6px 0 0" }}>
                                            {marketing.insightsDeAudiencia.map((ins, i) => <li key={i} style={{ fontSize: "0.85rem", marginBottom: 4 }}>{ins}</li>)}
                                        </ul>
                                    </div>
                                )}
                            </ZMCard>
                            {renderDecisionList("Ideas creativas", marketing.ideasCreativas, "Marketing")}
                            {renderDecisionList("Mensajes sugeridos", marketing.mensajesSugeridos, "Marketing")}
                            {renderDecisionList("Ángulos de venta", marketing.angulosDeVenta, "Marketing")}
                            {renderDecisionList("Ofertas a probar", marketing.ofertasAProbar, "Marketing")}
                            {renderDecisionList("Acciones prioritarias", marketing.accionesPrioritarias, "Marketing")}
                            {renderDecisionList("Alertas Marketing", marketing.alertas, "Marketing")}
                            {renderDecisionList("Experimentos recomendados", marketing.experimentosRecomendados, "Marketing")}
                        </>
                    )}

                    {/* ─── TAB CLOSERS ─── */}
                    {tab === "closers" && (
                        <>
                            {renderAgentFailure(closers.estado, closers.error, "Closers")}
                            {closers.accionesPrioritarias.length === 0 && closers.alertasSeguimiento.length === 0 && (
                                <div className="alert alert-warning py-2 px-3 mb-3 small">
                                    ⚠️ <strong>Tab sin datos.</strong> La IA no generó recomendaciones para Closers en este análisis. Presioná <button type="button" className="btn btn-sm btn-warning py-0 px-2 ms-1" onClick={generar} disabled={loading}>Regenerar</button>
                                </div>
                            )}
                            <ZMCard title="Diagnóstico Closers / Ventas" className="mb-3">
                                <p style={{ fontSize: "0.9rem", margin: "0 0 8px" }}>{closers.diagnostico || "-"}</p>
                                {closers.slaRecomendado && (
                                    <div className="small"><strong>SLA recomendado:</strong> {closers.slaRecomendado}</div>
                                )}
                            </ZMCard>
                            {renderDecisionList("Leads críticos", closers.leadsCriticos, "Closers")}
                            {renderDecisionList("Leads prioritarios", closers.leadsPrioritarios, "Closers")}
                            {renderDecisionList("Alertas de seguimiento", closers.alertasSeguimiento, "Closers")}
                            {renderDecisionList("Acciones prioritarias", closers.accionesPrioritarias, "Closers")}
                            {closers.scriptsDeContacto.length > 0 && (
                                <ZMCard title="Scripts de contacto" className="mb-3">
                                    <ul style={{ paddingLeft: 20, margin: 0 }}>
                                        {closers.scriptsDeContacto.map((x, i) => <li key={i} style={{ fontSize: "0.9rem", marginBottom: 6 }}>{x}</li>)}
                                    </ul>
                                </ZMCard>
                            )}
                            {closers.objecionesProbables.length > 0 && (
                                <ZMCard title="Objeciones probables" className="mb-3">
                                    <ul style={{ paddingLeft: 20, margin: 0 }}>
                                        {closers.objecionesProbables.map((x, i) => <li key={i} style={{ fontSize: "0.9rem", marginBottom: 6 }}>{x}</li>)}
                                    </ul>
                                </ZMCard>
                            )}
                            {closers.mejorasProcesoComercial.length > 0 && (
                                <ZMCard title="Mejoras al proceso comercial" className="mb-3">
                                    <ul style={{ paddingLeft: 20, margin: 0 }}>
                                        {closers.mejorasProcesoComercial.map((x, i) => <li key={i} style={{ fontSize: "0.9rem", marginBottom: 6 }}>{x}</li>)}
                                    </ul>
                                </ZMCard>
                            )}
                            {closers.checklistCalidad.length > 0 && (
                                <ZMCard title="Checklist de calidad comercial" className="mb-3">
                                    <ul style={{ paddingLeft: 20, margin: 0 }}>
                                        {closers.checklistCalidad.map((x, i) => <li key={i} style={{ fontSize: "0.9rem", marginBottom: 6 }}>{x}</li>)}
                                    </ul>
                                </ZMCard>
                            )}
                            {closers.sla.length > 0 && (
                                <ZMCard title="SLA y seguimiento" className="mb-3">
                                    <ul style={{ paddingLeft: 20, margin: 0 }}>
                                        {closers.sla.map((x, i) => <li key={i} style={{ fontSize: "0.9rem", marginBottom: 6 }}>{x}</li>)}
                                    </ul>
                                </ZMCard>
                            )}
                        </>
                    )}

                    {/* ─── TAB HISTORIAL ─── */}
                    {tab === "historial" && (
                        <>
                            {learningLoading && <ZMLoadingState label="Cargando historial..." />}
                            {!learningLoading && learningError && (
                                <ZMErrorState message={learningError} onRetry={() => loadLearning(getToken(), data?.analisisId || null)} />
                            )}
                            {!learningLoading && (
                                <>
                                    <ZMCard title="Reportes anteriores" className="mb-3">
                                        <ZMTable
                                            columns={[
                                                { key: "id", header: "ID" },
                                                { key: "fechaCreacion", header: "Fecha", render: (r) => r.fechaCreacion ? new Date(r.fechaCreacion).toLocaleString("es-AR") : "-" },
                                                { key: "campaniaId", header: "Campaña", render: (r) => r.campaniaId || "Todas" },
                                                { key: "scoreGeneral", header: "Score", render: (r) => <strong>{r.scoreGeneral || 0}</strong> },
                                                { key: "estadoGeneral", header: "Estado", render: (r) => <ZMBadge status={r.estadoGeneral?.toLowerCase()?.includes("crit") ? "paused" : r.estadoGeneral?.toLowerCase()?.includes("escal") ? "active" : "pending"} label={r.estadoGeneral || "-"} /> },
                                                { key: "resumenCorto", header: "Resumen", render: (r) => r.resumenCorto || "-" },
                                                {
                                                    key: "acciones", header: "Acciones",
                                                    render: (r) => (
                                                        <div className="d-flex gap-1">
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-primary"
                                                                onClick={() => loadHistorialReporte(r.id)}
                                                                disabled={reportLoading}
                                                            >
                                                                Ver reporte
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-success"
                                                                onClick={async () => {
                                                                    const token = getToken();
                                                                    if (!token) return;
                                                                    try {
                                                                        const rep = await fetchZocoMetaAiHistorialReporte(token, r.id);
                                                                        if (rep?.reporte) exportarReporteHtml(rep.reporte, `Reporte #${r.id} – ${new Date(r.fechaCreacion).toLocaleDateString("es-AR")}`);
                                                                    } catch { /* ignore */ }
                                                                }}
                                                            >
                                                                HTML
                                                            </button>
                                                        </div>
                                                    ),
                                                },
                                            ]}
                                            rows={historial}
                                            emptyText="Sin historial registrado."
                                        />
                                    </ZMCard>

                                    {selectedReport && (
                                        <ZMCard
                                            title={`Reporte #${selectedReport.id} — ${selectedReport.fechaCreacion ? new Date(selectedReport.fechaCreacion).toLocaleString("es-AR") : ""}`}
                                            className="mb-3"
                                        >
                                            <div className="d-flex gap-2 mb-3">
                                                <ZMBadge status="active" label={`Score: ${selectedReport.scoreGeneral || 0}`} />
                                                <ZMBadge status="pending" label={selectedReport.estadoGeneral || "-"} />
                                                {selectedReport.campaniaId && <ZMBadge status="pending" label={`Campaña: ${selectedReport.campaniaId}`} />}
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-success ms-auto"
                                                    onClick={() => exportarReporteHtml(selectedReport.reporte, `Reporte #${selectedReport.id}`)}
                                                >
                                                    Exportar HTML
                                                </button>
                                                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setSelectedReport(null)}>
                                                    Cerrar
                                                </button>
                                            </div>
                                            <p style={{ fontSize: "0.85rem", color: "#6b7280", margin: 0 }}>
                                                {selectedReport.reporte?.coordinador?.resumenEjecutivo || selectedReport.reporte?.resumenGeneral || "Reporte cargado. Usá 'Exportar HTML' para ver el reporte completo."}
                                            </p>
                                        </ZMCard>
                                    )}

                                    <ZMCard title="Reglas activas del negocio" className="mb-3">
                                        <div className="row g-2 mb-3">
                                            <div className="col-12 col-md-3">
                                                <input className="form-control form-control-sm" value={ruleForm.categoria} onChange={(e) => setRuleForm((s) => ({ ...s, categoria: e.target.value }))} placeholder="Categoria" />
                                            </div>
                                            <div className="col-12 col-md-3">
                                                <input className="form-control form-control-sm" value={ruleForm.titulo} onChange={(e) => setRuleForm((s) => ({ ...s, titulo: e.target.value }))} placeholder="Titulo" />
                                            </div>
                                            <div className="col-12 col-md-4">
                                                <input className="form-control form-control-sm" value={ruleForm.regla} onChange={(e) => setRuleForm((s) => ({ ...s, regla: e.target.value }))} placeholder="Regla de negocio" />
                                            </div>
                                            <div className="col-4 col-md-1">
                                                <input type="number" min="1" className="form-control form-control-sm" value={ruleForm.prioridad} onChange={(e) => setRuleForm((s) => ({ ...s, prioridad: Number(e.target.value || 1) }))} />
                                            </div>
                                            <div className="col-8 col-md-1">
                                                <button type="button" className="btn btn-sm zm-btn-primary w-100" onClick={onSaveRule} disabled={ruleSaving}>
                                                    {ruleForm.id > 0 ? "Editar" : "Agregar"}
                                                </button>
                                            </div>
                                        </div>
                                        {learningError && <div className="alert alert-danger py-2 px-3 small mb-2">{learningError}</div>}
                                        <ZMTable
                                            columns={[
                                                { key: "categoria", header: "Categoría" },
                                                { key: "titulo", header: "Título" },
                                                { key: "regla", header: "Regla" },
                                                { key: "prioridad", header: "P." },
                                                { key: "activo", header: "Activo", render: (row) => <ZMBadge status={row.activo ? "active" : "paused"} /> },
                                                {
                                                    key: "acciones", header: "",
                                                    render: (row) => (
                                                        <div className="d-flex gap-1">
                                                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setRuleForm({ id: row.id, categoria: row.categoria, titulo: row.titulo, regla: row.regla, prioridad: row.prioridad, activo: row.activo })}>Editar</button>
                                                            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => onDeleteRule(row.id)}>Desactivar</button>
                                                        </div>
                                                    ),
                                                },
                                            ]}
                                            rows={knowledge}
                                            emptyText="Sin reglas guardadas."
                                        />
                                    </ZMCard>

                                    <ZMCard title="Feedback recibido">
                                        <ZMTable
                                            columns={[
                                                { key: "fechaCreacion", header: "Fecha", render: (r) => r.fechaCreacion ? new Date(r.fechaCreacion).toLocaleString("es-AR") : "-" },
                                                { key: "agente", header: "Agente" },
                                                { key: "feedback", header: "Feedback" },
                                                { key: "recomendacion", header: "Recomendación" },
                                                { key: "comentario", header: "Comentario" },
                                            ]}
                                            rows={feedbackRows}
                                            emptyText="Sin feedback registrado."
                                        />
                                    </ZMCard>
                                </>
                            )}
                        </>
                    )}

                    {/* ─── TAB AUDITORÍA ─── */}
                    {tab === "auditoria" && (
                        <>
                            {(auditoriaDatos.resumen || auditoriaDatos.checksConsistencia.length > 0 || auditoriaDatos.crmVsMeta.length > 0 || auditoriaDatos.camposFaltantes.length > 0) && (
                                <ZMCard title="AuditorÃ­a del reporte IA" className="mb-3">
                                    {auditoriaDatos.resumen && (
                                        <p className="small mb-3">{auditoriaDatos.resumen}</p>
                                    )}
                                    {auditoriaDatos.checksConsistencia.length > 0 && (
                                        <>
                                            <strong className="small">Checks de consistencia</strong>
                                            <ul style={{ paddingLeft: 20, margin: "8px 0 12px" }}>
                                                {auditoriaDatos.checksConsistencia.map((item, idx) => <li key={`audit-check-${idx}`} style={{ marginBottom: 4 }}>{item}</li>)}
                                            </ul>
                                        </>
                                    )}
                                    {auditoriaDatos.crmVsMeta.length > 0 && (
                                        <>
                                            <strong className="small">CRM vs Meta</strong>
                                            <ul style={{ paddingLeft: 20, margin: "8px 0 12px" }}>
                                                {auditoriaDatos.crmVsMeta.map((item, idx) => <li key={`audit-crm-${idx}`} style={{ marginBottom: 4 }}>{item}</li>)}
                                            </ul>
                                        </>
                                    )}
                                    {auditoriaDatos.camposFaltantes.length > 0 && (
                                        <>
                                            <strong className="small">Campos faltantes</strong>
                                            <ul style={{ paddingLeft: 20, margin: "8px 0 0" }}>
                                                {auditoriaDatos.camposFaltantes.map((item, idx) => <li key={`audit-missing-${idx}`} style={{ marginBottom: 4 }}>{item}</li>)}
                                            </ul>
                                        </>
                                    )}
                                </ZMCard>
                            )}
                            {!debugContext && !debugLoading && (
                                <ZMCard className="mb-3" bodyClassName="p-4">
                                    <p className="text-muted small mb-3">
                                        Cargá la auditoría para ver exactamente qué datos estamos trayendo de Meta y CRM antes de enviárselos a la IA.
                                        No se exponen tokens ni secrets.
                                    </p>
                                    <button type="button" className="btn btn-sm zm-btn-primary" onClick={loadDebugContext}>
                                        Cargar auditoría de datos
                                    </button>
                                </ZMCard>
                            )}
                            {debugLoading && <ZMLoadingState label="Cargando auditoría de datos Meta + CRM..." />}
                            {!debugLoading && debugError && <ZMErrorState message={debugError} onRetry={loadDebugContext} />}

                            {debugContext && (
                                <>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <span className="small text-muted">Generado: {debugContext.generadoEn}</span>
                                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setDebugContext(null)}>
                                            Actualizar
                                        </button>
                                    </div>

                                    {/* Checks de consistencia */}
                                    {(debugContext.checksConsistencia || []).length > 0 && (
                                        <ZMCard title="Checks de consistencia" className="mb-3">
                                            {(debugContext.checksConsistencia || []).map((check, idx) => (
                                                <div
                                                    key={idx}
                                                    className="d-flex gap-3 mb-2 p-3"
                                                    style={{
                                                        background: check.prioridad === "urgente" ? "#fee2e2" : check.prioridad === "alta" ? "#fef3c7" : "#f9fafb",
                                                        borderRadius: 8,
                                                        borderLeft: `4px solid ${check.prioridad === "urgente" ? "#ef4444" : check.prioridad === "alta" ? "#f59e0b" : "#d1d5db"}`,
                                                    }}
                                                >
                                                    <div style={{ minWidth: 80 }}>
                                                        <span className={`badge bg-${renderChecksBadge(check.tipo)}`}>{check.prioridad}</span>
                                                    </div>
                                                    <div>
                                                        <strong style={{ fontSize: "0.88rem" }}>{check.mensaje}</strong>
                                                        <p style={{ fontSize: "0.8rem", color: "#6b7280", margin: "4px 0 0" }}>{check.impacto}</p>
                                                        <small style={{ color: "#9ca3af" }}>Fuente: {check.fuente}</small>
                                                    </div>
                                                </div>
                                            ))}
                                        </ZMCard>
                                    )}

                                    <div className="row g-3 mb-3">
                                        {/* Leads CRM */}
                                        <div className="col-12 col-md-6">
                                            <ZMCard title="Leads CRM" className="h-100">
                                                <div className="zm-grid-metrics" style={{ gridTemplateColumns: "repeat(2, 1fr)", gap: 10 }}>
                                                    {[
                                                        ["Total", debugContext.leadsCrm?.total],
                                                        ["Nuevos", debugContext.leadsCrm?.nuevos],
                                                        ["Contactados", debugContext.leadsCrm?.contactados],
                                                        ["Cerrados", debugContext.leadsCrm?.cerrados],
                                                        ["Sin campaña", debugContext.leadsCrm?.sinCampaniaId],
                                                        ["Duplicados", debugContext.leadsCrm?.duplicados],
                                                    ].map(([label, val]) => (
                                                        <div key={label} style={{ background: "#f9fafb", borderRadius: 8, padding: "10px 14px" }}>
                                                            <div style={{ fontSize: "0.72rem", color: "#6b7280", fontWeight: 600 }}>{label}</div>
                                                            <div style={{ fontSize: "1.3rem", fontWeight: 800, color: "#065f46" }}>{fmtNum(val)}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                                <p className="small text-muted mt-2 mb-0">Fuente: {debugContext.leadsCrm?.fuente}</p>
                                            </ZMCard>
                                        </div>

                                        {/* Campañas Meta */}
                                        <div className="col-12 col-md-6">
                                            <ZMCard title="Campañas Meta" className="h-100">
                                                <div className="zm-grid-metrics" style={{ gridTemplateColumns: "repeat(2, 1fr)", gap: 10 }}>
                                                    {[
                                                        ["Total", debugContext.campanias?.total],
                                                        ["Activas", debugContext.campanias?.activas],
                                                        ["Pausadas", debugContext.campanias?.pausadas],
                                                        ["Con insights", debugContext.insights?.totalCampaniasConInsights],
                                                        ["Ads con insights", debugContext.insights?.totalAnunciosConInsights],
                                                        ["Adsets", debugContext.adsets?.total],
                                                    ].map(([label, val]) => (
                                                        <div key={label} style={{ background: "#f9fafb", borderRadius: 8, padding: "10px 14px" }}>
                                                            <div style={{ fontSize: "0.72rem", color: "#6b7280", fontWeight: 600 }}>{label}</div>
                                                            <div style={{ fontSize: "1.3rem", fontWeight: 800, color: "#065f46" }}>{fmtNum(val)}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                                <div className="mt-2">
                                                    <span className="small text-muted">Breakdowns: </span>
                                                    <span className="small">{(debugContext.insights?.breakdownsDisponibles || []).join(", ")}</span>
                                                </div>
                                            </ZMCard>
                                        </div>
                                    </div>

                                    {/* Diferencias leads Meta vs CRM */}
                                    {(debugContext.diferenciasLeadsMeta || []).length > 0 && (
                                        <ZMCard title="Diferencias leads Meta vs CRM" className="mb-3">
                                            <p className="small text-muted mb-2">Campañas donde la atribución de Meta y los leads reales en CRM no coinciden.</p>
                                            <ZMTable
                                                columns={[
                                                    { key: "campaignName", header: "Campaña" },
                                                    { key: "leadsCrm", header: "Leads CRM" },
                                                    { key: "leadsMetaReported", header: "Leads Meta" },
                                                    { key: "diferencia", header: "Diferencia", render: (r) => <span style={{ color: r.diferencia !== 0 ? "#ef4444" : "#059669", fontWeight: 700 }}>{r.diferencia > 0 ? `+${r.diferencia}` : r.diferencia}</span> },
                                                    { key: "nota", header: "Nota" },
                                                ]}
                                                rows={debugContext.diferenciasLeadsMeta || []}
                                                emptyText="Sin diferencias detectadas."
                                            />
                                        </ZMCard>
                                    )}

                                    {/* Detalle campañas */}
                                    <ZMCard title="Detalle campañas Meta" className="mb-3">
                                        <ZMTable
                                            columns={[
                                                { key: "campaignName", header: "Campaña" },
                                                { key: "status", header: "Estado", render: (r) => <ZMBadge status={r.status === "ACTIVE" ? "active" : "paused"} label={r.status} /> },
                                                { key: "spend", header: "Inversión", render: (r) => fmtUsd(r.spend) },
                                                { key: "leadsCrm", header: "Leads CRM" },
                                                { key: "leadsMetaReported", header: "Leads Meta" },
                                                { key: "cpl", header: "CPL CRM", render: (r) => r.cpl > 0 ? fmtUsd(r.cpl) : "-" },
                                                { key: "ctr", header: "CTR", render: (r) => r.ctr > 0 ? fmtPct(r.ctr) : "-" },
                                            ]}
                                            rows={(debugContext.campanias?.detalle || []).slice(0, 30)}
                                            emptyText="Sin campañas."
                                        />
                                    </ZMCard>
                                </>
                            )}
                        </>
                    )}
                </>
            )}

            {/* Historial y auditoría accesibles aunque no haya análisis generado */}
            {!loading && !error && !data && (
                <div className="mt-3">
                    <div className="zm-ia-tabs mb-3">
                        {[{ key: "historial", label: "Historial" }, { key: "auditoria", label: "Auditoría" }].map((item) => (
                            <button
                                key={item.key}
                                type="button"
                                className={`btn btn-sm ${tab === item.key ? "zm-btn-primary" : "btn-outline-secondary"}`}
                                onClick={() => {
                                    setTab(item.key);
                                    if (item.key === "historial" && historial.length === 0) loadLearning(getToken());
                                }}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>

                    {tab === "historial" && (
                        <>
                            {learningLoading && <ZMLoadingState label="Cargando historial..." />}
                            {!learningLoading && (
                                <ZMCard title="Reportes anteriores">
                                    <ZMTable
                                        columns={[
                                            { key: "id", header: "ID" },
                                            { key: "fechaCreacion", header: "Fecha", render: (r) => r.fechaCreacion ? new Date(r.fechaCreacion).toLocaleString("es-AR") : "-" },
                                            { key: "campaniaId", header: "Campaña", render: (r) => r.campaniaId || "Todas" },
                                            { key: "scoreGeneral", header: "Score" },
                                            { key: "estadoGeneral", header: "Estado" },
                                            {
                                                key: "acciones", header: "",
                                                render: (r) => (
                                                    <div className="d-flex gap-1">
                                                        <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => loadHistorialReporte(r.id)} disabled={reportLoading}>Ver</button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-success"
                                                            onClick={async () => {
                                                                const token = getToken();
                                                                if (!token) return;
                                                                try {
                                                                    const rep = await fetchZocoMetaAiHistorialReporte(token, r.id);
                                                                    if (rep?.reporte) exportarReporteHtml(rep.reporte, `Reporte #${r.id}`);
                                                                } catch { /* ignore */ }
                                                            }}
                                                        >HTML</button>
                                                    </div>
                                                ),
                                            },
                                        ]}
                                        rows={historial}
                                        emptyText="Sin historial registrado."
                                    />
                                    {selectedReport && (
                                        <div className="mt-3 p-3" style={{ background: "#f9fafb", borderRadius: 8, border: "1px solid #e5e7eb" }}>
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <strong>Reporte #{selectedReport.id}</strong>
                                                <div className="d-flex gap-2">
                                                    <button type="button" className="btn btn-sm btn-outline-success" onClick={() => exportarReporteHtml(selectedReport.reporte, `Reporte #${selectedReport.id}`)}>Exportar HTML</button>
                                                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setSelectedReport(null)}>Cerrar</button>
                                                </div>
                                            </div>
                                            <p className="small text-muted mb-0">{selectedReport.reporte?.coordinador?.resumenEjecutivo || selectedReport.reporte?.resumenGeneral || "Reporte cargado. Usá 'Exportar HTML' para ver el detalle completo."}</p>
                                        </div>
                                    )}
                                </ZMCard>
                            )}
                        </>
                    )}

                    {tab === "auditoria" && (
                        <>
                            {!debugContext && !debugLoading && (
                                <ZMCard className="mb-3" bodyClassName="p-4">
                                    <p className="text-muted small mb-3">Cargá la auditoría para ver los datos Meta + CRM sin llamar a la IA.</p>
                                    <button type="button" className="btn btn-sm zm-btn-primary" onClick={loadDebugContext}>Cargar auditoría</button>
                                </ZMCard>
                            )}
                            {debugLoading && <ZMLoadingState label="Cargando auditoría..." />}
                            {debugError && <ZMErrorState message={debugError} onRetry={loadDebugContext} />}
                            {debugContext && (
                                <ZMCard title="Checks de consistencia">
                                    {(debugContext.checksConsistencia || []).map((check, idx) => (
                                        <div key={idx} className="d-flex gap-3 mb-2 p-3" style={{ background: "#f9fafb", borderRadius: 8, borderLeft: `4px solid ${check.prioridad === "urgente" ? "#ef4444" : "#f59e0b"}` }}>
                                            <span className={`badge bg-${renderChecksBadge(check.tipo)}`}>{check.prioridad}</span>
                                            <div>
                                                <strong style={{ fontSize: "0.88rem" }}>{check.mensaje}</strong>
                                                <p style={{ fontSize: "0.8rem", color: "#6b7280", margin: "4px 0 0" }}>{check.impacto}</p>
                                            </div>
                                        </div>
                                    ))}
                                    {(debugContext.checksConsistencia || []).length === 0 && <p className="text-muted small mb-0">Sin inconsistencias detectadas.</p>}
                                </ZMCard>
                            )}
                        </>
                    )}
                </div>
            )}
        </ZMLayout>
    );
}
